import { licenseIssues, stringList, type LicenseIssue } from "./license-issues";
import type {
  VerificationAssessmentItem,
  VerificationCheck,
  VerificationResult,
} from "./types";

export type Evidence = VerificationCheck["evidenceManifest"][number];
type Attachment = VerificationCheck["attachments"][number];

export const EVIDENCE_KIND = {
  LicenseProfile: "license-profile",
  SourceData: "source-data",
  Diagnostic: "diagnostic",
} as const;
export const PNG_MEDIA_TYPE = "image/png";
export const PDF_MEDIA_TYPE = "application/pdf";
const JSON_MEDIA_TYPE = "application/json";
/** Sources whose reviewed official dataset record is the license evidence. */
const SOURCE_RECORD_LICENSE_SOURCES = new Set([
  "il-physician",
  "il-physician-assistant",
  "wa-physician",
  "wa-physician-assistant",
  "al-physician",
]);
/** FSMB's own report PDF is the license proof for every state it reports,
 * as the server's `isStateLicenseEvidence` decides. */
const FSMB_SOURCE_ID = "fsmb-pdc";

export function getLicenseEvidence(
  check: VerificationCheck,
  result: VerificationResult,
) {
  const evidenceKey = result.facts.licenseEvidenceKey;
  if (typeof evidenceKey !== "string") {
    return undefined;
  }
  return check.evidenceManifest.find(
    (evidence) =>
      evidence.key === evidenceKey &&
      isLicenseEvidence(check.sourceId, evidence) &&
      result.evidenceKeys.includes(evidence.key),
  );
}

function isLicenseEvidence(sourceId: string, evidence: Evidence) {
  if (sourceId === FSMB_SOURCE_ID) {
    return (
      evidence.kind === EVIDENCE_KIND.LicenseProfile &&
      evidence.mediaType === PDF_MEDIA_TYPE
    );
  }
  if (
    evidence.kind === EVIDENCE_KIND.LicenseProfile &&
    evidence.mediaType === PNG_MEDIA_TYPE
  ) {
    return true;
  }
  return (
    SOURCE_RECORD_LICENSE_SOURCES.has(sourceId) &&
    evidence.kind === EVIDENCE_KIND.SourceData &&
    evidence.mediaType === JSON_MEDIA_TYPE
  );
}

/** Every attachment on the check that belongs to this result. */
export function getResultAttachments(
  check: VerificationCheck,
  result: VerificationResult,
) {
  return check.attachments.filter(
    (attachment) => attachment.resultKey === result.resultKey,
  );
}

/** The result's attachments that were made from its own license evidence;
 * none without validated evidence. */
export function getLicenseAttachments(
  check: VerificationCheck,
  result: VerificationResult,
): Attachment[] {
  const evidence = getLicenseEvidence(check, result);
  if (!evidence) {
    return [];
  }
  return getResultAttachments(check, result).filter(
    (attachment) => attachment.evidenceFileUploadId === evidence.fileUploadId,
  );
}

export function getAssessmentItem(
  check: VerificationCheck,
  result: VerificationResult,
) {
  return check.assessment.items.find(
    (item) => item.resultKey === result.resultKey,
  );
}

/** The server-owned assessment carries every license caveat, including ones it
 * derives (such as unavailable evidence). Disciplinary caveats are shown with
 * the screening result they qualify, so they are excluded here. */
export function getLicenseLimitations({
  result,
  assessment,
}: {
  result: VerificationResult;
  assessment?: VerificationAssessmentItem;
}) {
  const shownElsewhere = new Set([
    ...stringList(result.facts.disciplinaryLimitations),
    ...(assessment?.disciplinary?.limitations ?? []),
  ]);
  return (
    assessment?.limitations ?? stringList(result.facts.limitations)
  ).filter((limitation) => !shownElsewhere.has(limitation));
}

/** The issues one check result raises, shared by the credential record and
 * the run report so both name the same problems. */
export function getResultIssues(
  check: VerificationCheck,
  result: VerificationResult,
): LicenseIssue[] {
  const assessment = getAssessmentItem(check, result);
  return licenseIssues({
    result,
    assessment,
    attachments: getLicenseAttachments(check, result),
    licenseLimitations: getLicenseLimitations({ result, assessment }),
  });
}
